/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import { useComponentProps } from '@sitecore-content-sdk/nextjs';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import ArticleTagsWrapper from 'helpers/GenericWrappers/ArticleTagsWrapper/ArticleTagsWrapper';
import formatDate from 'lib/utils/date-formatter';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { EditingConditionalRender } from 'helpers/Editing/EditingConditionalRender';
import useIsEditing from 'lib/hooks/useIsEditing';
import { getTestProps } from 'lib/testing/utils';
const ArticleContent = (props)=>{
    const ctxProps = useComponentProps(props?.rendering?.uid);
    const staticProps = props?.staticProps ?? ctxProps?.staticProps;
    const category = staticProps?.parentCategory;
    const articleDetail = staticProps?.articleDetail;
    const fields = articleDetail?.fields || {};
    const publishedDate = formatDate(fields.publishedDate?.value);
    const tags = Array.isArray(fields.SxaTags) ? fields.SxaTags : [];
    const stringTagsArray = tags?.map((tag)=>tag?.fields?.Title?.value) || [];
    const { getDictionaryValue } = useDictionary();
    const { base, imageWithDetailContainer, imageStyle, authorDetails, authorStyleWrapper, authorHeadStyleText, authorStyleText, metaInfo } = TAILWIND_VARIANTS();
    const isEditingMode = useIsEditing();
    const isMetaDataContainerVisible = !!fields.authorName?.value || !!publishedDate || !!category;
    return /*#__PURE__*/ _jsxs("div", {
        className: base(),
        ...getTestProps(`component-article-content-${articleDetail?.id}`),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: imageWithDetailContainer(),
                children: [
                    fields.image && /*#__PURE__*/ _jsx(ImageWrapper, {
                        className: imageStyle(),
                        field: fields.image,
                        priority: true,
                        ...getTestProps(`image`)
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: authorDetails({
                            isMetaDataContainerVisible: isMetaDataContainerVisible || isEditingMode
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(EditingConditionalRender, {
                                showIf: !!fields.authorName?.value,
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: authorStyleWrapper(),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: authorHeadStyleText(),
                                            ...getTestProps(`author-name-label`),
                                            children: getDictionaryValue('WrittenBy') || 'Written by'
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: authorStyleText(),
                                            ...getTestProps(`author-name`),
                                            children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                field: fields?.authorName,
                                                tag: "div"
                                            })
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(EditingConditionalRender, {
                                showIf: !!publishedDate,
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: authorStyleWrapper(),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: authorHeadStyleText(),
                                            ...getTestProps(`published-date-label`),
                                            children: getDictionaryValue('PublishedOn') || 'Published on'
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: authorStyleText(),
                                            ...getTestProps(`published-date`),
                                            children: publishedDate
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(EditingConditionalRender, {
                                showIf: !!category,
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: authorStyleWrapper(),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: authorHeadStyleText(),
                                            ...getTestProps(`category-label`),
                                            children: getDictionaryValue('Category') || 'Category'
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: authorStyleText(),
                                            ...getTestProps(`category`),
                                            children: category || 'Uncategorized'
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: metaInfo(),
                children: [
                    /*#__PURE__*/ _jsx(RichTextWrapper, {
                        field: fields?.description,
                        tag: "p",
                        ...getTestProps(`description`)
                    }),
                    /*#__PURE__*/ _jsx(ArticleTagsWrapper, {
                        tags: stringTagsArray,
                        ...getTestProps(`article-tags-wrapper`)
                    })
                ]
            })
        ]
    });
};
export default ArticleContent;
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'flex',
            'flex-col'
        ],
        imageWithDetailContainer: [
            'flex',
            'flex-col',
            'gap-component-article-hero-image-margin-bottom',
            'border-b',
            'border-b-width-width-025',
            'border-color-border-border'
        ],
        imageStyle: [
            'object-cover',
            'w-full',
            'aspect-[21/9]',
            'rounded-xl'
        ],
        authorDetails: [
            'flex',
            'gap-space-between-more'
        ],
        authorStyleWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8'
        ],
        authorHeadStyleText: [
            'text-color-icon-dark',
            'font-typography-font-family-body',
            'text-typography-font-size-body-small',
            'font-semibold'
        ],
        authorStyleText: [
            'text-color-text-text',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'font-normal'
        ],
        metaInfo: [
            'container',
            'max-w-4xl',
            'mx-auto',
            'pt-general-spacing-margin-y',
            'flex',
            'flex-col',
            'gap-margin-more'
        ]
    },
    variants: {
        isMetaDataContainerVisible: {
            true: {
                authorDetails: [
                    'mb-component-article-hero-image-margin-bottom'
                ]
            },
            false: {
                authorDetails: [
                    'mb-0'
                ]
            }
        }
    }
});
